import { useQuery } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { usePublicFetch } from './usePublicFetch';
import { useOTPAuth } from '../auth/providers/OTPAuthProvider';
import { API_URL } from '../utils/config';

// Data interfaces
export interface Continent {
  id: string;
  name: string;
  code: string;
  isActive?: boolean;
  countryCount: number;
  marketCount: number;
}

export interface Country {
  id: string;
  name: string;
  code: string;
  continentId: string;
  marketCount: number;
}

export interface Market {
  id: string;
  name: string;
  countryId: string;
  isActive: boolean;
  slug: string;
  productImage: string;
  productImage2?: string;
  productImage3?: string;
  sortOrder: number;
  subscription?: {
    productId: string;
    productName: string;
    description: string;
    prices: Array<{
      priceId: string;
      amount: number;
      currency: string;
      interval: 'month' | 'year';
    }>;
  };
}

export interface MarketSearchResult extends Market {
  country: {
    id: string;
    name: string;
    code: string;
  };
  continent: {
    id: string;
    name: string;
  };
  relevanceScore: number;
}

export interface MarketDetails extends Market {
  description: string;
  reportSections: Array<{
    category: string;
    categoryDescription?: string;
    sections: Array<{
      title: string;
      description: string;
    }>;
  }>;
  country: {
    id: string;
    name: string;
    code: string;
  };
  continent: {
    id: string;
    name: string;
    code: string;
  };
  relatedMarkets: Array<{
    id: string;
    name: string;
    slug: string;
    relationType: string;
  }>;
  subscription?: {
    productId: string;
    productName: string;
    description: string;
    prices: Array<{
      priceId: string;
      amount: number;
      currency: string;
      interval: 'month' | 'year';
    }>;
  };
}

// API Response wrapper
interface ApiResponse<T> {
  success: boolean;
  data: T;
  metadata?: any;
}

// Custom hooks for MarketCompass API
export const useContinents = () => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const isAuthenticated = accounts.length > 0;

  return useQuery<Continent[], Error>({
    queryKey: ['continents', isAuthenticated],
    queryFn: async (): Promise<Continent[]> => {
      try {
        const url = `${API_URL}/api/store/markets/continents`;
        // Use authenticated fetch if logged in, otherwise use public fetch
        const response = isAuthenticated 
          ? await authenticatedFetch(url)
          : await publicFetch(url);
        
        if (!response) {
          console.error('Failed to fetch continents: No response');
          return [];
        }
        
        if (!response.ok) {
          console.error(`Failed to fetch continents: HTTP ${response.status} ${response.statusText}`);
          return [];
        }

        const result: ApiResponse<Continent[]> = await response.json();
        return result.data || [];
      } catch (error) {
        console.error('Continents fetch error:', error);
        throw error;
      }
    },
    enabled: true, // Always enabled now, works for both authenticated and unauthenticated
    staleTime: 10 * 60 * 1000, // Consider continents data fresh for 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
};

export const useCountriesByContinent = (continentId: string | undefined) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const isAuthenticated = accounts.length > 0;

  return useQuery<Country[], Error>({
    queryKey: ['countries', continentId, isAuthenticated],
    queryFn: async (): Promise<Country[]> => {
      try {
        if (!continentId) {
          return [];
        }

        const url = `${API_URL}/api/store/markets/countries/${continentId}`;
        // Use authenticated fetch if logged in, otherwise use public fetch
        const response = isAuthenticated 
          ? await authenticatedFetch(url)
          : await publicFetch(url);
        
        if (!response) {
          console.error('Failed to fetch countries: No response');
          return [];
        }
        
        if (!response.ok) {
          console.error(`Failed to fetch countries: HTTP ${response.status} ${response.statusText}`);
          return [];
        }

        const result: ApiResponse<Country[]> = await response.json();
        return result.data || [];
      } catch (error) {
        console.error('Countries fetch error:', error);
        throw error;
      }
    },
    enabled: !!continentId, // Enable for both authenticated and unauthenticated users
    staleTime: 10 * 60 * 1000, // Consider countries data fresh for 10 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
};

export const useMarketsByCountry = (countryId: string | undefined) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const isAuthenticated = accounts.length > 0;

  return useQuery<Market[], Error>({
    queryKey: ['markets', countryId, isAuthenticated],
    queryFn: async (): Promise<Market[]> => {
      try {
        if (!countryId) {
          return [];
        }

        const url = `${API_URL}/api/store/markets/${countryId}`;
        // Use authenticated fetch if logged in, otherwise use public fetch
        const response = isAuthenticated 
          ? await authenticatedFetch(url)
          : await publicFetch(url);
        
        if (!response) {
          console.error('Failed to fetch markets: No response');
          return [];
        }
        
        if (!response.ok) {
          console.error(`Failed to fetch markets: HTTP ${response.status} ${response.statusText}`);
          return [];
        }

        const result: ApiResponse<Market[]> = await response.json();
        return result.data || [];
      } catch (error) {
        console.error('Markets fetch error:', error);
        throw error;
      }
    },
    enabled: !!countryId, // Enable for both authenticated and unauthenticated users
    staleTime: 5 * 60 * 1000, // Consider markets data fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
  });
};

export const useMarketSearch = (query: string, options?: { limit?: number; category?: string }) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const isAuthenticated = accounts.length > 0;

  return useQuery<MarketSearchResult[], Error>({
    queryKey: ['marketSearch', query, options, isAuthenticated],
    queryFn: async (): Promise<MarketSearchResult[]> => {
      try {
        if (!query || query.trim().length < 2) {
          return [];
        }

        const params = new URLSearchParams();
        params.append('query', query.trim());
        if (options?.limit) params.append('limit', options.limit.toString());
        if (options?.category) params.append('category', options.category);

        const url = `${API_URL}/api/store/markets/search?${params.toString()}`;
        // Use authenticated fetch if logged in, otherwise use public fetch
        const response = isAuthenticated 
          ? await authenticatedFetch(url)
          : await publicFetch(url);
        
        if (!response) {
          console.error('Failed to search markets: No response');
          return [];
        }
        
        if (!response.ok) {
          console.error(`Failed to search markets: HTTP ${response.status} ${response.statusText}`);
          return [];
        }

        const result: ApiResponse<MarketSearchResult[]> = await response.json();
        return result.data || [];
      } catch (error) {
        console.error('Market search error:', error);
        throw error;
      }
    },
    enabled: !!query && query.trim().length >= 2, // Enable for both authenticated and unauthenticated users
    staleTime: 2 * 60 * 1000, // Consider search results fresh for 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
};

export const useMarketBySlug = (continentSlug?: string, countrySlug?: string, marketSlug?: string) => {
  const { accounts } = useMsal();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { publicFetch } = usePublicFetch();
  const isAuthenticated = accounts.length > 0;

  return useQuery<MarketDetails | null, Error>({
    queryKey: ['marketBySlug', continentSlug, countrySlug, marketSlug, isAuthenticated],
    queryFn: async (): Promise<MarketDetails | null> => {
      try {
        if (!continentSlug || !countrySlug || !marketSlug) {
          return null;
        }

        const url = `${API_URL}/api/store/markets/by-slug/${continentSlug}/${countrySlug}/${marketSlug}`;
        // Use authenticated fetch if logged in, otherwise use public fetch
        const response = isAuthenticated 
          ? await authenticatedFetch(url)
          : await publicFetch(url);
        
        if (!response) {
          console.error('Failed to fetch market by slug: No response');
          return null;
        }
        
        if (!response.ok) {
          console.error(`Failed to fetch market by slug: HTTP ${response.status} ${response.statusText}`);
          return null;
        }

        const result: ApiResponse<MarketDetails> = await response.json();
        return result.data || null;
      } catch (error) {
        console.error('Market by slug fetch error:', error);
        throw error;
      }
    },
    enabled: !!continentSlug && !!countrySlug && !!marketSlug, // Enable for both authenticated and unauthenticated users
    staleTime: 5 * 60 * 1000, // Consider market data fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
  });
};

export const useMarketDetails = (marketId: string | undefined) => {
  const { accounts } = useMsal();
  const { isAuthenticated: isOTPAuthenticated } = useOTPAuth();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const isAuthenticated = accounts.length > 0 || isOTPAuthenticated;

  return useQuery<MarketDetails, Error>({
    queryKey: ['marketDetails', marketId],
    queryFn: async (): Promise<MarketDetails> => {
      try {
        if (!isAuthenticated || !marketId) {
          throw new Error('No authenticated user or market ID');
        }

        const url = `${API_URL}/api/marketcompass/markets/${marketId}/details`;
        const response = await authenticatedFetch(url);
        
        if (!response) {
          throw new Error('Failed to fetch market details: No response from authenticatedFetch');
        }
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch market details: HTTP ${response.status} - ${errorText}`);
        }

        const result: ApiResponse<MarketDetails> = await response.json();
        if (!result.data) {
          throw new Error('No market details data received');
        }
        
        return result.data;
      } catch (error) {
        console.error('Market details fetch error:', error);
        throw error;
      }
    },
    enabled: isAuthenticated && !!marketId,
    staleTime: 5 * 60 * 1000, // Consider market details fresh for 5 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
  });
};